import { useCallback, useEffect, useState } from "react";

import { useOptionalAccessToken } from "../../../context/AuthContext";
import { fmt, getMessages } from "../../../i18n";
import { invalidateUploadLimit, resolveUploadLimitBytes } from "../../../services/uploadLimit";
import {
    ABSOLUTE_MAX_UPLOAD_BYTES,
    FileSizeRejection,
    formatLimitLabel,
    isFileSizeAllowed,
    partitionBySize,
} from "../../../utils/uploadLimits";
import { BILLING_REFRESHED } from "../../layout/BillingReturnSnackbar";

/**
 * State holder + ergonomic wrappers around the per-file size cap.
 *
 * Two surfaces it covers:
 *   - `filterFiles`: pre-flight check for surfaces that *receive* a list
 *     of files (drag-and-drop / file input). Returns only the accepted
 *     files; the rejected ones are stashed for the toast.
 *   - `guardUploadFile`: decorates an editor's `uploadFile` callback so
 *     a single oversize file is rejected before the network call. Both
 *     records the rejection for the toast AND throws so BlockNote's
 *     internal upload pipeline can show its built-in error UI.
 *
 * Pair with `FileSizeRejectionSnackbar` and pass `rejection` /
 * `dismissRejection` straight through.
 *
 * The cap is the caller's TIER cap, resolved here rather than by each
 * consumer — every call site is a bare `useFileSizeGuard()`, so none of
 * the thirteen editors had to change when this stopped being a flat
 * constant. Resolution is shared and cached (`services/uploadLimit`), so
 * the many editors that mount together cost one request between them.
 */
export const useFileSizeGuard = () => {
    const accessToken = useOptionalAccessToken();
    const [rejection, setRejection] = useState<FileSizeRejection | null>(null);
    // `null` until the tier is known. Reads go through `limitBytes`,
    // which treats unknown as PERMISSIVE — a guard that hasn't loaded
    // must never be stricter than the server, or a slow first paint
    // reproduces the flat-5 MB bug this replaced.
    const [tierLimitBytes, setTierLimitBytes] = useState<number | null>(null);
    const limitBytes = tierLimitBytes ?? ABSOLUTE_MAX_UPLOAD_BYTES;

    useEffect(() => {
        let alive = true;
        const load = () => {
            void resolveUploadLimitBytes(accessToken).then((bytes) => {
                if (alive) setTierLimitBytes(bytes);
            });
        };
        load();
        // An upgrade is the one thing that moves this number, and it can
        // happen while these editors stay mounted (the keep-alive Homes
        // never unmount), so the TTL alone would leave a freshly-upgraded
        // user on their old ceiling.
        const onBillingRefreshed = () => {
            invalidateUploadLimit();
            load();
        };
        window.addEventListener(BILLING_REFRESHED, onBillingRefreshed);
        return () => {
            alive = false;
            window.removeEventListener(BILLING_REFRESHED, onBillingRefreshed);
        };
    }, [accessToken]);

    const dismissRejection = useCallback(() => setRejection(null), []);

    const reportRejection = useCallback((next: FileSizeRejection) => {
        // Coalesce concurrent rejections into the same toast — e.g. a
        // user drops 5 oversize files; we'd rather show one snackbar
        // with all 5 than spawn 5 stacking ones. The dedup is keyed on
        // (name, size) so genuinely-distinct dropped files still all
        // appear, but a noisy retry of the same file doesn't double up.
        setRejection((prev) => {
            if (!prev) return next;
            const seen = new Set(prev.files.map((f) => `${f.name} ${f.size}`));
            const merged = [...prev.files];
            for (const file of next.files) {
                const key = `${file.name} ${file.size}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    merged.push(file);
                }
            }
            // Newest limit wins: if the tier resolved between two
            // rejections, the number the user is shown must be the one
            // actually being applied.
            return { files: merged, limitBytes: next.limitBytes };
        });
    }, []);

    const filterFiles = useCallback(
        (files: Iterable<File>): File[] => {
            const { accepted, rejected } = partitionBySize(files, limitBytes);
            if (rejected) reportRejection(rejected);
            return accepted;
        },
        [reportRejection, limitBytes]
    );

    /**
     * Wrap `uploadFile(file)` so it rejects oversize files before any
     * fetch happens. The thrown Error is what BlockNote (and the
     * `pendingFiles` loop in `bnChatEditor` / `bnThreadEditor`) catches
     * to mark the slot as failed.
     */
    const guardUploadFile = useCallback(
        <R>(uploadFile: (file: File) => Promise<R>) => {
            return async (file: File): Promise<R> => {
                if (!isFileSizeAllowed(file, limitBytes)) {
                    reportRejection({ files: [{ name: file.name, size: file.size }], limitBytes });
                    throw new Error(
                        fmt(getMessages().common.editor.fileSizeExceeded, {
                            name: file.name,
                            label: formatLimitLabel(limitBytes),
                        })
                    );
                }
                return uploadFile(file);
            };
        },
        [reportRejection, limitBytes]
    );

    return {
        rejection,
        dismissRejection,
        reportRejection,
        filterFiles,
        guardUploadFile,
        /** The ceiling currently applied, in bytes — for surfaces that
         *  stage files themselves instead of calling the wrappers above
         *  (the v3 composer's pending strip). */
        limitBytes,
    };
};
